<template>
  <div :class="embedded ? 'ticket-login-embedded-root' : 'role-page d-flex'">
    <aside v-if="!embedded" class="side-nav">
      <h4 class="mb-3">管理员系统</h4>
      <button class="sub-btn active" type="button">准考证登录页管理</button>
      <div class="mt-auto">
        <button class="btn btn-success w-100 side-nav-logout-btn" type="button" @click="logout">退出登录</button>
      </div>
    </aside>

    <main class="content-area" :class="{ 'ticket-login-embedded-main': embedded }">
      <header v-if="!embedded" class="topbar mb-3">
        <div>
          <h2 class="mb-1">准考证登录管理</h2>
          <p class="mb-0 text-muted">登录页配置、准考证批量生成与学生关联</p>
        </div>
      </header>

      <div class="ticket-toolbar d-flex flex-wrap gap-2 mb-3" role="tablist" aria-label="准考证登录管理分区">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="btn"
          :class="activeTab === tab.key ? 'btn-primary' : 'btn-light'"
          :aria-selected="activeTab === tab.key"
          @click="switchTab(tab.key)"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- 统计条 -->
      <div class="ticket-stats-bar small mb-3" v-if="stats">
        <span>学生总数 <strong>{{ stats.studentTotal }}</strong></span>
        <span class="ticket-stats-sep">·</span>
        <span>已绑准考证 <strong>{{ stats.studentWithTicket }}</strong></span>
        <span class="ticket-stats-sep">·</span>
        <span>未绑 <strong>{{ stats.studentWithoutTicket }}</strong></span>
        <span class="ticket-stats-sep">·</span>
        <span>库存准考证 <strong>{{ stats.ticketTotal }}</strong>（未关联 {{ stats.ticketUnbound }}）</span>
      </div>

      <!-- Tab1: 登录页 -->
      <template v-if="activeTab === 'loginPage'">
        <section class="card-box city-paper-card ticket-section mb-3">
          <div class="ticket-section-head">
            <div class="ticket-section-head__text">
              <h4 class="ticket-section-title">登录页配置列表</h4>
              <p class="ticket-section-desc">
                生成对外访问链接，供考生使用准考证号登录。可复制链接或直接打开预览。
              </p>
            </div>
            <button class="btn btn-primary btn-sm ticket-section-head__action" type="button" @click="openCreate">
              新建登录页
            </button>
          </div>
          <div class="city-paper-result-shell">
            <table class="table table-striped align-middle modern-table mb-0">
              <thead>
                <tr>
                  <th>名称</th>
                  <th>标题</th>
                  <th>登录页背景图</th>
                  <th>状态</th>
                  <th>访问链接</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rows" :key="'ticket-page-' + row.id">
                  <td class="ticket-cell-wrap">{{ row.pageName || '-' }}</td>
                  <td class="ticket-cell-wrap">{{ row.pageTitle || '-' }}</td>
                  <td>
                    <div v-if="row.bgImageThumbUrl || row.bgImageJpgUrl || row.bgImageUrl" class="ticket-bg-thumb-wrap">
                      <img
                        class="ticket-bg-thumb"
                        :src="row.bgImageThumbUrl || row.bgImageJpgUrl || row.bgImageUrl"
                        alt="背景预览"
                      />
                      <span class="small text-muted">已配置</span>
                    </div>
                    <span v-else class="small text-muted">默认背景</span>
                  </td>
                  <td>
                    <span class="ticket-status-badge" :class="Number(row.status) === 1 ? 'is-on' : 'is-off'">
                      {{ Number(row.status) === 1 ? '开启' : '关闭' }}
                    </span>
                  </td>
                  <td>
                    <div class="ticket-action-row">
                      <button class="btn btn-sm btn-light" type="button" @click="copyLink(row)">复制链接</button>
                      <button class="btn btn-sm btn-primary" type="button" @click="openLink(row)">打开网址</button>
                    </div>
                  </td>
                  <td>
                    <div class="ticket-action-row">
                      <button class="btn btn-sm btn-light" type="button" @click="openEdit(row)">编辑</button>
                      <button class="btn btn-sm btn-light" type="button" @click="toggleStatus(row)">
                        {{ Number(row.status) === 1 ? '关闭' : '开启' }}
                      </button>
                      <button class="btn btn-sm btn-danger ticket-danger-btn" type="button" @click="removeRow(row)">删除</button>
                    </div>
                  </td>
                </tr>
                <tr v-if="rows.length === 0">
                  <td colspan="6" class="text-muted py-4">暂无配置，请点击右上角「新建登录页」生成访问地址。</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section v-if="showForm" class="card-box city-paper-card ticket-section mb-3">
          <div class="ticket-section-head">
            <div class="ticket-section-head__text">
              <h4 class="ticket-section-title">{{ form.id ? '编辑登录页' : '新建登录页' }}</h4>
              <p class="ticket-section-desc">填写展示文案、背景图与状态后保存；访问链接仍由系统自动生成，与登录校验逻辑无关。</p>
            </div>
          </div>
          <div class="ticket-form-panel">
            <div class="row g-3">
              <div class="col-md-6 col-lg-3">
                <div class="ticket-field">
                  <label class="ticket-field-label">配置名称</label>
                  <input v-model.trim="form.pageName" class="form-control" placeholder="管理端显示名称" />
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="ticket-field">
                  <label class="ticket-field-label">页面标题</label>
                  <input v-model.trim="form.pageTitle" class="form-control" placeholder="准考证登录" />
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="ticket-field">
                  <label class="ticket-field-label">副标题</label>
                  <input v-model.trim="form.pageSubtitle" class="form-control" placeholder="请输入准考证号完成登录" />
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="ticket-field">
                  <label class="ticket-field-label">状态</label>
                  <select v-model="form.status" class="form-select">
                    <option :value="1">开启</option>
                    <option :value="0">关闭</option>
                  </select>
                </div>
              </div>
              <div class="col-12">
                <div class="ticket-field">
                  <label class="ticket-field-label">提示文案</label>
                  <textarea
                    v-model.trim="form.hintText"
                    class="form-control"
                    rows="3"
                    placeholder="展示在登录表单附近，可填写考试须知等说明"
                  />
                </div>
              </div>
              <div class="col-12">
                <div class="ticket-field">
                  <label class="ticket-field-label">登录页背景图</label>
                  <p class="ticket-field-hint">
                    仅作用于该配置对应的考生登录链接。上传后自动压缩为 WebP（≤500KB，宽 1920px），并生成 JPG 兜底与低清缩略图。管理端本页不使用此背景。
                  </p>
                  <div class="ticket-bg-editor">
                    <div v-if="form.bgImageThumbUrl || form.bgImageJpgUrl || form.bgImageUrl" class="ticket-bg-preview-box">
                      <img
                        :src="form.bgImageThumbUrl || form.bgImageJpgUrl || form.bgImageUrl"
                        alt="背景预览"
                        class="ticket-bg-preview-img"
                      />
                      <div class="ticket-action-row mt-2">
                        <a
                          v-if="form.bgImageUrl || form.bgImageJpgUrl"
                          class="btn btn-sm btn-light"
                          :href="form.bgImageJpgUrl || form.bgImageUrl"
                          target="_blank"
                          rel="noopener"
                        >预览大图</a>
                        <button class="btn btn-sm btn-danger ticket-danger-btn" type="button" :disabled="bgUploading" @click="clearBgImage">
                          删除背景图
                        </button>
                      </div>
                    </div>
                    <div v-else class="ticket-bg-empty">当前使用系统默认背景</div>
                    <div class="ticket-action-row mt-2">
                      <label class="btn btn-sm btn-primary mb-0" :class="{ disabled: bgUploading }">
                        {{ bgUploading ? '上传压缩中…' : '上传/替换背景图' }}
                        <input
                          type="file"
                          class="d-none"
                          accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                          :disabled="bgUploading"
                          @change="onBgFileChange"
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="form.pageRoute" class="ticket-stats-bar small mt-3">
              <div>
                <div>访问路由：{{ form.pageRoute }}</div>
                <div class="text-muted mt-1">完整地址将按当前域名自动生成（规则同地市考试报名系统）。</div>
              </div>
            </div>
            <div class="ticket-form-actions">
              <button class="btn btn-primary" type="button" :disabled="saving || bgUploading" @click="saveForm">
                {{ saving ? '保存中…' : '保存' }}
              </button>
              <button class="btn btn-light" type="button" :disabled="saving || bgUploading" @click="closeForm">取消</button>
            </div>
          </div>
        </section>
      </template>

      <!-- Tab2: 生成 -->
      <template v-if="activeTab === 'generate'">
        <section class="card-box city-paper-card ticket-section mb-3">
          <div class="ticket-section-head">
            <div class="ticket-section-head__text">
              <h4 class="ticket-section-title">编码规则</h4>
              <p class="ticket-section-desc">设置前缀与序号位数，下方两种生成方式共用此规则。</p>
            </div>
          </div>
          <div class="ticket-form-panel">
            <div class="row g-3">
              <div class="col-md-6 col-lg-2">
                <div class="ticket-field">
                  <label class="ticket-field-label">前缀</label>
                  <input v-model.trim="genRule.prefix" class="form-control" placeholder="如 ZK" maxlength="20" />
                </div>
              </div>
              <div class="col-md-6 col-lg-2">
                <div class="ticket-field">
                  <label class="ticket-field-label">序号位数</label>
                  <input v-model.number="genRule.digitLen" type="number" min="3" max="12" class="form-control" />
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="ticket-field">
                  <label class="ticket-field-label">批次名称</label>
                  <input v-model.trim="genRule.batchName" class="form-control" placeholder="可选" />
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="ticket-field">
                  <label class="ticket-field-label">备注</label>
                  <input v-model.trim="genRule.remark" class="form-control" placeholder="可选" />
                </div>
              </div>
              <div class="col-md-6 col-lg-2">
                <div class="ticket-field">
                  <label class="ticket-field-label">规则样例</label>
                  <div class="ticket-rule-sample">{{ ruleSample }}</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="card-box city-paper-card ticket-section mb-3">
          <div class="ticket-section-head">
            <div class="ticket-section-head__text">
              <h4 class="ticket-section-title">1. 按已有学生账号批量生成并绑定</h4>
              <p class="ticket-section-desc">
                依据系统内启用中的学生账号，一对一自动生成准考证并写回账号；建议仅处理尚未绑定准考证的学生。
              </p>
            </div>
          </div>
          <div class="ticket-form-panel">
            <div class="form-check ticket-check mb-3">
              <input id="onlyWithoutTicket" v-model="genRule.onlyWithoutTicket" class="form-check-input" type="checkbox" />
              <label class="form-check-label" for="onlyWithoutTicket">仅给未绑定准考证的学生生成（推荐）</label>
            </div>
            <button class="btn btn-primary" type="button" :disabled="generating" @click="generateForStudents">
              {{ generating ? '生成中…' : '批量生成并绑定' }}
            </button>
          </div>
        </section>

        <section class="card-box city-paper-card ticket-section mb-3">
          <div class="ticket-section-head">
            <div class="ticket-section-head__text">
              <h4 class="ticket-section-title">2. 独立批量生成准考证</h4>
              <p class="ticket-section-desc">
                按规则预生成准考证库存，可导出 Excel 后再关联学生；也可用于教师端按准考证分配试卷。
              </p>
            </div>
          </div>
          <div class="ticket-form-panel">
            <div class="row g-3 align-items-end">
              <div class="col-md-4 col-lg-3">
                <div class="ticket-field">
                  <label class="ticket-field-label">生成数量</label>
                  <input v-model.number="genRule.quantity" type="number" min="1" max="20000" class="form-control" />
                </div>
              </div>
              <div class="col-md-4 col-lg-3">
                <button class="btn btn-primary" type="button" :disabled="generating" @click="generateIndependent">
                  {{ generating ? '生成中…' : '独立批量生成' }}
                </button>
              </div>
            </div>
          </div>
        </section>
      </template>

      <!-- Tab3: 记录 -->
      <template v-if="activeTab === 'records'">
        <section class="card-box city-paper-card ticket-section mb-3">
          <div class="ticket-section-head">
            <div class="ticket-section-head__text">
              <h4 class="ticket-section-title">生成记录</h4>
              <p class="ticket-section-desc">查看历史生成批次，可导出 Excel 或查看明细。</p>
            </div>
            <div class="d-flex flex-wrap align-items-center gap-2">
              <label class="ticket-field-label mb-0">每页</label>
              <select
                v-model.number="batchPageSize"
                class="form-select form-select-sm"
                style="width: auto; min-width: 72px"
                @change="searchBatches"
              >
                <option :value="10">10</option>
                <option :value="20">20</option>
                <option :value="50">50</option>
              </select>
              <span class="small text-muted">
                共 {{ batchTotalElements }} 条，第 {{ batchPage + 1 }} / {{ batchTotalPages }} 页
              </span>
              <button class="btn btn-light btn-sm" type="button" :disabled="batchLoading" @click="loadBatches">刷新</button>
            </div>
          </div>
          <div class="city-paper-result-shell">
            <table class="table table-striped align-middle modern-table mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>批次名称</th>
                  <th>模式</th>
                  <th>规则样例</th>
                  <th>生成/绑定</th>
                  <th>操作人</th>
                  <th>时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="b in batches" :key="'batch-' + b.id">
                  <td>{{ b.id }}</td>
                  <td>{{ b.batchName || '-' }}</td>
                  <td>{{ b.generateModeLabel || b.generateMode }}</td>
                  <td>{{ b.ruleSample || '-' }}</td>
                  <td>{{ b.generatedCount || 0 }} / {{ b.boundCount || 0 }}</td>
                  <td>{{ b.operatorName || '-' }}</td>
                  <td>{{ formatTime(b.createTime) }}</td>
                  <td>
                    <div class="ticket-action-row">
                      <button class="btn btn-sm btn-light" type="button" @click="viewBatchItems(b)">查看明细</button>
                      <button class="btn btn-sm btn-primary" type="button" @click="exportBatch(b)">导出Excel</button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!batchLoading && batches.length === 0">
                  <td colspan="8" class="text-muted">暂无生成记录</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="d-flex flex-wrap justify-content-end align-items-center gap-2 mt-3">
            <div class="btn-group">
              <button type="button" class="btn btn-sm btn-light" :disabled="batchPage <= 0 || batchLoading" @click="goBatchPage(0)">
                首页
              </button>
              <button
                type="button"
                class="btn btn-sm btn-light"
                :disabled="batchPage <= 0 || batchLoading"
                @click="goBatchPage(batchPage - 1)"
              >
                上一页
              </button>
              <button type="button" class="btn btn-sm btn-light" disabled>
                {{ batchPage + 1 }} / {{ batchTotalPages }}
              </button>
              <button
                type="button"
                class="btn btn-sm btn-light"
                :disabled="batchPage >= batchTotalPages - 1 || batchLoading"
                @click="goBatchPage(batchPage + 1)"
              >
                下一页
              </button>
              <button
                type="button"
                class="btn btn-sm btn-light"
                :disabled="batchPage >= batchTotalPages - 1 || batchLoading"
                @click="goBatchPage(batchTotalPages - 1)"
              >
                末页
              </button>
            </div>
          </div>
        </section>

        <section v-if="selectedBatch" class="card-box city-paper-card mb-3">
          <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <h4 class="mb-0">批次明细 #{{ selectedBatch.id }} · {{ selectedBatch.batchName }}</h4>
            <button class="btn btn-sm btn-light" type="button" @click="selectedBatch = null">关闭</button>
          </div>
          <div class="city-paper-result-shell">
            <table class="table table-striped align-middle modern-table mb-0">
              <thead>
                <tr>
                  <th>准考证号</th>
                  <th>状态</th>
                  <th>学生账号</th>
                  <th>姓名</th>
                  <th>学生ID</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="it in batchItems" :key="'item-' + it.id">
                  <td>{{ it.ticketNo }}</td>
                  <td>
                    <span class="ticket-status-badge" :class="Number(it.bindStatus) === 1 ? 'is-on' : 'is-off'">
                      {{ Number(it.bindStatus) === 1 ? '已绑定' : '未绑定' }}
                    </span>
                  </td>
                  <td>{{ it.username || '-' }}</td>
                  <td>{{ it.realName || '-' }}</td>
                  <td>{{ it.userId || '-' }}</td>
                </tr>
                <tr v-if="batchItems.length === 0">
                  <td colspan="5" class="text-muted">无明细</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="d-flex justify-content-end align-items-center gap-2 mt-3">
            <div class="btn-group">
              <button class="btn btn-sm btn-light" type="button" :disabled="itemPage <= 0" @click="changeItemPage(itemPage - 1)">
                上一页
              </button>
              <button type="button" class="btn btn-sm btn-light" disabled>
                {{ itemPage + 1 }} / {{ itemTotalPages }}
              </button>
              <button
                class="btn btn-sm btn-light"
                type="button"
                :disabled="itemPage >= itemTotalPages - 1"
                @click="changeItemPage(itemPage + 1)"
              >
                下一页
              </button>
            </div>
          </div>
        </section>
      </template>

      <!-- Tab4: 关联 -->
      <template v-if="activeTab === 'bind'">
        <section class="card-box city-paper-card ticket-section mb-3">
          <div class="ticket-section-head">
            <div class="ticket-section-head__text">
              <h4 class="ticket-section-title">单条关联</h4>
              <p class="ticket-section-desc">将已生成的准考证号绑定到指定学生账号。</p>
            </div>
          </div>
          <div class="ticket-form-panel">
            <div class="row g-3 align-items-end">
              <div class="col-md-4">
                <div class="ticket-field">
                  <label class="ticket-field-label">准考证号</label>
                  <input v-model.trim="bindForm.ticketNo" class="form-control" placeholder="已生成的准考证" />
                </div>
              </div>
              <div class="col-md-4">
                <div class="ticket-field">
                  <label class="ticket-field-label">学生账号</label>
                  <input v-model.trim="bindForm.username" class="form-control" placeholder="学生登录账号" />
                </div>
              </div>
              <div class="col-md-3 col-lg-2">
                <button class="btn btn-primary w-100" type="button" :disabled="binding" @click="bindOne">
                  {{ binding ? '关联中…' : '关联' }}
                </button>
              </div>
            </div>
          </div>
        </section>

        <section class="card-box city-paper-card ticket-section mb-3">
          <div class="ticket-section-head">
            <div class="ticket-section-head__text">
              <h4 class="ticket-section-title">Excel 批量关联</h4>
              <p class="ticket-section-desc">A 列准考证号，B 列学生账号。可将独立生成的准考证批量关联到已有学生。</p>
            </div>
          </div>
          <div class="ticket-form-panel">
            <div class="row g-3 align-items-end">
              <div class="col-md-6">
                <div class="ticket-field">
                  <label class="ticket-field-label">选择 Excel 文件</label>
                  <input type="file" class="form-control" accept=".xlsx" @change="onBindExcelChange" />
                </div>
              </div>
              <div class="col-md-6 d-flex flex-wrap gap-2">
                <button class="btn btn-primary" type="button" :disabled="binding" @click="bindExcel">上传并关联</button>
                <button class="btn btn-light" type="button" @click="downloadBindTemplate">下载模板</button>
              </div>
            </div>
          </div>
        </section>
      </template>
    </main>
  </div>
</template>

<script>
export default {
  name: 'AdminTicketLoginPageManageView',
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tabs: [
        { key: 'loginPage', label: '登录页配置' },
        { key: 'generate', label: '准考证生成' },
        { key: 'records', label: '生成记录' },
        { key: 'bind', label: '关联学生' }
      ],
      activeTab: 'loginPage',
      stats: null,
      rows: [],
      showForm: false,
      saving: false,
      bgUploading: false,
      form: {
        id: null,
        pageName: '准考证登录页',
        pageTitle: '准考证登录',
        pageSubtitle: '请输入准考证号完成登录',
        hintText: '',
        status: 1,
        pageToken: '',
        pageRoute: '',
        bgImageUrl: '',
        bgImageJpgUrl: '',
        bgImageThumbUrl: ''
      },
      genRule: {
        prefix: 'ZK',
        digitLen: 6,
        batchName: '',
        remark: '',
        onlyWithoutTicket: true,
        quantity: 100
      },
      generating: false,
      batches: [],
      batchPage: 0,
      batchPageSize: 10,
      batchTotalPages: 1,
      batchTotalElements: 0,
      batchLoading: false,
      selectedBatch: null,
      batchItems: [],
      itemPage: 0,
      itemTotalPages: 1,
      bindForm: { ticketNo: '', username: '' },
      bindExcelFile: null,
      binding: false
    }
  },
  computed: {
    ruleSample() {
      const p = String(this.genRule.prefix || '')
      let len = Number(this.genRule.digitLen || 6)
      if (!Number.isFinite(len) || len < 3) len = 3
      if (len > 12) len = 12
      return `${p}${String(1).padStart(len, '0')}`
    }
  },
  created() {
    this.loadRows()
    this.loadStats()
  },
  methods: {
    currentUser() {
      try {
        return JSON.parse(localStorage.getItem('exam_user') || '{}') || {}
      } catch (e) {
        return {}
      }
    },
    authHeaders(extra = {}) {
      const u = this.currentUser()
      return {
        'Content-Type': 'application/json',
        'X-Role': String(u.role || 'admin'),
        'X-User-Id': String(u.id || ''),
        'X-Username': String(u.username || ''),
        'X-CSRF-Token': '1',
        ...extra
      }
    },
    authHeadersNoJson() {
      const h = this.authHeaders()
      delete h['Content-Type']
      return h
    },
    logout() {
      localStorage.removeItem('exam_user')
      localStorage.removeItem('exam_token')
      this.$router.push('/login')
    },
    switchTab(key) {
      this.activeTab = key
      if (key === 'records') this.loadBatches()
      if (key === 'generate' || key === 'records' || key === 'bind') this.loadStats()
    },
    formatTime(v) {
      if (!v) return '-'
      return String(v).replace('T', ' ').slice(0, 19)
    },
    async loadStats() {
      try {
        const res = await fetch('/api/admin/ticket-manage/stats', { headers: this.authHeaders() }).then((r) => r.json())
        if (res.code === 200) this.stats = res.data
      } catch (e) {
        void e
      }
    },
    absLink(row) {
      if (!row) return ''
      const route = String(row.pageRoute || '').trim()
      if (!route) return ''
      const base = `${window.location.origin}${window.location.pathname}`
      if (route.startsWith('/#/')) return `${window.location.origin}${route}`
      if (route.startsWith('#/')) return `${base}${route}`
      if (route.startsWith('http://') || route.startsWith('https://')) return route
      return `${window.location.origin}/#/${route.replace(/^\/+/, '')}`
    },
    copyLink(row) {
      const link = this.absLink(row)
      if (!link) {
        window.alert('链接尚未生成')
        return
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(link)
          .then(() => window.alert('准考证登录地址已复制'))
          .catch(() => window.alert(`请手动复制：${link}`))
      } else {
        window.alert(`请手动复制：${link}`)
      }
    },
    openLink(row) {
      const link = this.absLink(row)
      if (!link) return
      window.open(link, '_blank', 'noopener,noreferrer')
    },
    openCreate() {
      this.form = {
        id: null,
        pageName: '准考证登录页',
        pageTitle: '准考证登录',
        pageSubtitle: '请输入准考证号完成登录',
        hintText: '',
        status: 1,
        pageToken: '',
        pageRoute: '',
        bgImageUrl: '',
        bgImageJpgUrl: '',
        bgImageThumbUrl: ''
      }
      this.showForm = true
    },
    openEdit(row) {
      this.form = {
        id: row.id,
        pageName: row.pageName || '',
        pageTitle: row.pageTitle || '',
        pageSubtitle: row.pageSubtitle || '',
        hintText: row.hintText || '',
        status: Number(row.status) === 0 ? 0 : 1,
        pageToken: row.pageToken || '',
        pageRoute: row.pageRoute || '',
        bgImageUrl: row.bgImageUrl || '',
        bgImageJpgUrl: row.bgImageJpgUrl || '',
        bgImageThumbUrl: row.bgImageThumbUrl || ''
      }
      this.showForm = true
    },
    closeForm() {
      this.showForm = false
    },
    async loadRows() {
      try {
        const res = await fetch('/api/crud/exam-ticket-login-page?page=0&size=200&sort=id,desc', {
          headers: this.authHeaders()
        }).then((r) => r.json())
        if (res.code !== 200) {
          window.alert(res.message || '加载失败')
          return
        }
        const data = res.data
        this.rows = Array.isArray(data) ? data : (data && data.content) || []
      } catch (e) {
        window.alert('加载准考证登录页配置失败，请确认后端已启动')
      }
    },
    async saveForm() {
      if (!String(this.form.pageName || '').trim()) {
        window.alert('请填写配置名称')
        return
      }
      this.saving = true
      try {
        const payload = {
          pageName: this.form.pageName,
          pageTitle: this.form.pageTitle,
          pageSubtitle: this.form.pageSubtitle,
          hintText: this.form.hintText,
          status: Number(this.form.status) === 0 ? 0 : 1,
          bgImageUrl: this.form.bgImageUrl || null,
          bgImageJpgUrl: this.form.bgImageJpgUrl || null,
          bgImageThumbUrl: this.form.bgImageThumbUrl || null
        }
        if (this.form.id && this.form.pageToken) {
          payload.pageToken = this.form.pageToken
          payload.pageRoute = this.form.pageRoute || `/#/ticket-login/${this.form.pageToken}`
        }
        const url = this.form.id
          ? `/api/crud/exam-ticket-login-page/${this.form.id}`
          : '/api/crud/exam-ticket-login-page'
        const method = this.form.id ? 'PUT' : 'POST'
        const res = await fetch(url, {
          method,
          headers: this.authHeaders(),
          body: JSON.stringify(payload)
        }).then((r) => r.json())
        if (res.code !== 200) {
          window.alert(res.message || '保存失败')
          return
        }
        this.showForm = false
        await this.loadRows()
        window.alert('保存成功，访问地址已自动生成')
      } catch (e) {
        window.alert('保存失败，请稍后重试')
      } finally {
        this.saving = false
      }
    },
    async toggleStatus(row) {
      const next = Number(row.status) === 1 ? 0 : 1
      try {
        const res = await fetch(`/api/crud/exam-ticket-login-page/${row.id}`, {
          method: 'PUT',
          headers: this.authHeaders(),
          body: JSON.stringify({
            pageName: row.pageName,
            pageTitle: row.pageTitle,
            pageSubtitle: row.pageSubtitle,
            hintText: row.hintText,
            status: next,
            pageToken: row.pageToken,
            pageRoute: row.pageRoute,
            bgImageUrl: row.bgImageUrl || null,
            bgImageJpgUrl: row.bgImageJpgUrl || null,
            bgImageThumbUrl: row.bgImageThumbUrl || null
          })
        }).then((r) => r.json())
        if (res.code !== 200) {
          window.alert(res.message || '更新状态失败')
          return
        }
        await this.loadRows()
      } catch (e) {
        window.alert('更新状态失败')
      }
    },
    async removeRow(row) {
      if (!window.confirm(`确认删除「${row.pageName || row.id}」吗？删除后原链接将失效。`)) return
      try {
        if (row.bgImageUrl || row.bgImageJpgUrl || row.bgImageThumbUrl) {
          const qs = new URLSearchParams()
          if (row.id) qs.set('pageId', String(row.id))
          await fetch(`/api/admin/ticket-login-bg?${qs.toString()}`, {
            method: 'DELETE',
            headers: this.authHeaders()
          }).catch(() => null)
        }
        const res = await fetch(`/api/crud/exam-ticket-login-page/${row.id}`, {
          method: 'DELETE',
          headers: this.authHeaders()
        }).then((r) => r.json())
        if (res.code !== 200) {
          window.alert(res.message || '删除失败')
          return
        }
        await this.loadRows()
      } catch (e) {
        window.alert('删除失败')
      }
    },
    async onBgFileChange(e) {
      const file = e && e.target && e.target.files && e.target.files[0]
      if (e && e.target) e.target.value = ''
      if (!file) return
      this.bgUploading = true
      try {
        const fd = new FormData()
        fd.append('file', file)
        let url = '/api/admin/ticket-login-bg/upload'
        if (this.form.id) {
          url += `?pageId=${encodeURIComponent(this.form.id)}`
        }
        const res = await fetch(url, {
          method: 'POST',
          headers: this.authHeadersNoJson(),
          body: fd
        }).then((r) => r.json())
        if (res.code !== 200 || !res.data) {
          window.alert(res.message || '背景图上传失败')
          return
        }
        this.form.bgImageUrl = res.data.bgImageUrl || ''
        this.form.bgImageJpgUrl = res.data.bgImageJpgUrl || ''
        this.form.bgImageThumbUrl = res.data.bgImageThumbUrl || ''
        if (this.form.id) {
          await this.loadRows()
        }
        window.alert('背景图已上传并压缩完成（WebP/JPG ≤500KB）')
      } catch (err) {
        window.alert('背景图上传失败，请稍后重试')
      } finally {
        this.bgUploading = false
      }
    },
    async clearBgImage() {
      if (!window.confirm('确认删除该登录页背景图？删除后考生端将使用默认背景。')) return
      this.bgUploading = true
      try {
        const qs = new URLSearchParams()
        if (this.form.id) {
          qs.set('pageId', String(this.form.id))
        } else {
          if (this.form.bgImageUrl) qs.set('bgImageUrl', this.form.bgImageUrl)
          if (this.form.bgImageJpgUrl) qs.set('bgImageJpgUrl', this.form.bgImageJpgUrl)
          if (this.form.bgImageThumbUrl) qs.set('bgImageThumbUrl', this.form.bgImageThumbUrl)
        }
        const res = await fetch(`/api/admin/ticket-login-bg?${qs.toString()}`, {
          method: 'DELETE',
          headers: this.authHeaders()
        }).then((r) => r.json())
        if (res.code !== 200) {
          window.alert(res.message || '删除失败')
          return
        }
        this.form.bgImageUrl = ''
        this.form.bgImageJpgUrl = ''
        this.form.bgImageThumbUrl = ''
        if (this.form.id) await this.loadRows()
      } catch (e) {
        window.alert('删除失败')
      } finally {
        this.bgUploading = false
      }
    },
    async generateForStudents() {
      if (!this.genRule.onlyWithoutTicket) {
        if (!window.confirm('将覆盖已有准考证绑定，确认继续？')) return
      } else {
        if (!window.confirm('确认为未绑定准考证的学生批量生成并一对一绑定？')) return
      }
      this.generating = true
      try {
        const res = await fetch('/api/admin/ticket-manage/generate-for-students', {
          method: 'POST',
          headers: this.authHeaders(),
          body: JSON.stringify({
            batchName: this.genRule.batchName,
            prefix: this.genRule.prefix,
            digitLen: this.genRule.digitLen,
            onlyWithoutTicket: !!this.genRule.onlyWithoutTicket,
            remark: this.genRule.remark
          })
        }).then((r) => r.json())
        if (res.code !== 200) {
          window.alert(res.message || '生成失败')
          return
        }
        const d = res.data || {}
        window.alert(`生成完成：共 ${d.generatedCount || 0} 个，已绑定 ${d.boundCount || 0} 人（批次 #${d.batchId}）`)
        this.activeTab = 'records'
        this.batchPage = 0
        await this.loadBatches()
        await this.loadStats()
      } catch (e) {
        window.alert('生成失败，请稍后重试')
      } finally {
        this.generating = false
      }
    },
    async generateIndependent() {
      const qty = Number(this.genRule.quantity || 0)
      if (!Number.isFinite(qty) || qty < 1) {
        window.alert('请填写有效的生成数量')
        return
      }
      if (!window.confirm(`确认按规则「${this.ruleSample}…」独立生成 ${qty} 个准考证？`)) return
      this.generating = true
      try {
        const res = await fetch('/api/admin/ticket-manage/generate-independent', {
          method: 'POST',
          headers: this.authHeaders(),
          body: JSON.stringify({
            batchName: this.genRule.batchName,
            prefix: this.genRule.prefix,
            digitLen: this.genRule.digitLen,
            quantity: qty,
            remark: this.genRule.remark
          })
        }).then((r) => r.json())
        if (res.code !== 200) {
          window.alert(res.message || '生成失败')
          return
        }
        const d = res.data || {}
        window.alert(`独立生成完成：${d.generatedCount || 0} 个（批次 #${d.batchId}），可在生成记录中导出`)
        this.activeTab = 'records'
        this.batchPage = 0
        await this.loadBatches()
        await this.loadStats()
      } catch (e) {
        window.alert('生成失败，请稍后重试')
      } finally {
        this.generating = false
      }
    },
    async loadBatches() {
      this.batchLoading = true
      try {
        const params = new URLSearchParams({
          page: String(this.batchPage || 0),
          size: String(this.batchPageSize || 10)
        })
        const res = await fetch(`/api/admin/ticket-manage/batches?${params.toString()}`, {
          headers: this.authHeaders()
        }).then((r) => r.json())
        if (res.code !== 200) {
          window.alert(res.message || '加载批次失败')
          this.batches = []
          this.batchTotalElements = 0
          this.batchTotalPages = 1
          return
        }
        const data = res.data
        if (Array.isArray(data)) {
          this.batches = data
          this.batchTotalElements = data.length
          this.batchTotalPages = 1
          return
        }
        this.batches = (data && data.content) || []
        this.batchTotalElements = Number((data && data.totalElements) || 0)
        this.batchTotalPages = Math.max(1, Number((data && data.totalPages) || 1))
        if (this.batchPage > this.batchTotalPages - 1) {
          this.batchPage = Math.max(0, this.batchTotalPages - 1)
        }
      } catch (e) {
        window.alert('加载生成记录失败')
        this.batches = []
      } finally {
        this.batchLoading = false
      }
    },
    searchBatches() {
      this.batchPage = 0
      this.loadBatches()
    },
    goBatchPage(next) {
      const page = Number(next)
      if (!Number.isFinite(page) || page < 0) return
      if (page === this.batchPage) return
      if (page >= this.batchTotalPages) return
      this.batchPage = page
      this.loadBatches()
    },
    async viewBatchItems(batch) {
      this.selectedBatch = batch
      this.itemPage = 0
      await this.loadBatchItems()
    },
    async changeItemPage(p) {
      this.itemPage = p
      await this.loadBatchItems()
    },
    async loadBatchItems() {
      if (!this.selectedBatch) return
      try {
        const res = await fetch(
          `/api/admin/ticket-manage/batches/${this.selectedBatch.id}/items?page=${this.itemPage}&size=20`,
          { headers: this.authHeaders() }
        ).then((r) => r.json())
        if (res.code !== 200) {
          window.alert(res.message || '加载明细失败')
          return
        }
        const data = res.data || {}
        this.batchItems = data.content || []
        this.itemTotalPages = Math.max(1, Number(data.totalPages || 1))
      } catch (e) {
        window.alert('加载明细失败')
      }
    },
    exportBatch(batch) {
      fetch(`/api/admin/ticket-manage/batches/${batch.id}/export`, { headers: this.authHeadersNoJson() })
        .then(async (r) => {
          if (!r.ok) {
            window.alert('导出失败')
            return
          }
          const blob = await r.blob()
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `ticket_batch_${batch.id}.xlsx`
          a.click()
          URL.revokeObjectURL(url)
        })
        .catch(() => window.alert('导出失败'))
    },
    async bindOne() {
      if (!this.bindForm.ticketNo || !this.bindForm.username) {
        window.alert('请填写准考证号与学生账号')
        return
      }
      this.binding = true
      try {
        const res = await fetch('/api/admin/ticket-manage/bind', {
          method: 'POST',
          headers: this.authHeaders(),
          body: JSON.stringify({
            ticketNo: this.bindForm.ticketNo,
            username: this.bindForm.username
          })
        }).then((r) => r.json())
        if (res.code !== 200) {
          window.alert(res.message || '关联失败')
          return
        }
        window.alert('关联成功')
        this.bindForm = { ticketNo: '', username: '' }
        await this.loadStats()
      } catch (e) {
        window.alert('关联失败')
      } finally {
        this.binding = false
      }
    },
    onBindExcelChange(e) {
      this.bindExcelFile = (e.target.files && e.target.files[0]) || null
    },
    bindExcel() {
      if (!this.bindExcelFile) {
        window.alert('请先选择Excel')
        return
      }
      const form = new FormData()
      form.append('file', this.bindExcelFile)
      this.binding = true
      fetch('/api/admin/ticket-manage/bind-excel', {
        method: 'POST',
        headers: this.authHeadersNoJson(),
        body: form
      })
        .then((r) => r.json())
        .then(async (res) => {
          if (res.code !== 200) {
            window.alert(res.message || '批量关联失败')
            return
          }
          const d = res.data || {}
          let msg = `成功 ${d.success || 0}，跳过 ${d.skipped || 0}`
          if (d.errors && d.errors.length) msg += `\n${d.errors.slice(0, 5).join('\n')}`
          window.alert(msg)
          await this.loadStats()
        })
        .catch(() => window.alert('批量关联失败'))
        .finally(() => {
          this.binding = false
        })
    },
    downloadBindTemplate() {
      fetch('/api/admin/ticket-manage/bind-template', { headers: this.authHeadersNoJson() })
        .then(async (r) => {
          if (!r.ok) {
            window.alert('下载失败')
            return
          }
          const blob = await r.blob()
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = 'ticket_bind_template.xlsx'
          a.click()
          URL.revokeObjectURL(url)
        })
        .catch(() => window.alert('下载失败'))
    }
  }
}
</script>

<style scoped>
.ticket-login-embedded-root,
.ticket-login-embedded-main {
  width: 100%;
}

.ticket-toolbar {
  margin-bottom: 16px !important;
}

.ticket-toolbar .btn {
  border-radius: 10px !important;
  font-weight: 600 !important;
  min-width: 108px;
}

.ticket-stats-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  margin-bottom: 16px !important;
  padding: 12px 14px;
  border: 1px dashed #c6dbff;
  border-radius: 12px;
  background: #f5f9ff;
  color: #355b9f;
  line-height: 1.5;
}

.ticket-stats-bar strong {
  color: #1f2a44;
  font-weight: 700;
}

.ticket-stats-sep {
  color: #9bb4db;
}

/* 覆盖全站 App.vue 对按钮的统一浅色抹平，恢复主/次/危险按钮层次 */
.ticket-login-embedded-root :deep(.btn),
.content-area :deep(.btn) {
  border-radius: 10px !important;
  font-weight: 600 !important;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.12s ease,
    filter 0.18s ease !important;
  box-shadow: none !important;
}

.ticket-login-embedded-root :deep(.btn:hover),
.content-area :deep(.btn:hover) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(47, 111, 235, 0.16) !important;
}

.ticket-login-embedded-root :deep(.btn:active),
.content-area :deep(.btn:active) {
  transform: translateY(0) !important;
  box-shadow: inset 0 2px 4px rgba(15, 23, 42, 0.12) !important;
  filter: brightness(0.96);
}

.ticket-login-embedded-root :deep(.btn:focus-visible),
.content-area :deep(.btn:focus-visible) {
  outline: 0 !important;
  box-shadow: 0 0 0 0.2rem rgba(47, 111, 235, 0.28) !important;
}

.ticket-login-embedded-root :deep(.btn:disabled),
.ticket-login-embedded-root :deep(.btn.disabled),
.content-area :deep(.btn:disabled),
.content-area :deep(.btn.disabled) {
  opacity: 0.55 !important;
  transform: none !important;
  box-shadow: none !important;
  cursor: not-allowed !important;
}

.ticket-login-embedded-root :deep(.btn-primary),
.content-area :deep(.btn-primary) {
  background: linear-gradient(120deg, #2f6feb, #5ca2ff) !important;
  border-color: #2f6feb !important;
  color: #fff !important;
}

.ticket-login-embedded-root :deep(.btn-primary:hover),
.content-area :deep(.btn-primary:hover) {
  background: linear-gradient(120deg, #2762d6, #4f96f5) !important;
  border-color: #2762d6 !important;
  color: #fff !important;
}

.ticket-login-embedded-root :deep(.btn-primary:active),
.content-area :deep(.btn-primary:active) {
  background: #2559c4 !important;
  border-color: #2559c4 !important;
  color: #fff !important;
}

.ticket-login-embedded-root :deep(.btn-light),
.content-area :deep(.btn-light) {
  background: #edf4ff !important;
  border-color: #c8dcff !important;
  color: #2f5da5 !important;
}

.ticket-login-embedded-root :deep(.btn-light:hover),
.content-area :deep(.btn-light:hover) {
  background: #deebff !important;
  border-color: #b4d0ff !important;
  color: #244f90 !important;
}

.ticket-login-embedded-root :deep(.btn-light:active),
.content-area :deep(.btn-light:active) {
  background: #d0e3ff !important;
  border-color: #a6c6f8 !important;
  color: #1f4479 !important;
}

.ticket-login-embedded-root :deep(.btn-danger),
.ticket-login-embedded-root :deep(.ticket-danger-btn),
.content-area :deep(.btn-danger),
.content-area :deep(.ticket-danger-btn) {
  background: #dc3545 !important;
  border-color: #dc3545 !important;
  color: #fff !important;
}

.ticket-login-embedded-root :deep(.btn-danger:hover),
.ticket-login-embedded-root :deep(.ticket-danger-btn:hover),
.content-area :deep(.btn-danger:hover),
.content-area :deep(.ticket-danger-btn:hover) {
  background: #c82333 !important;
  border-color: #bd2130 !important;
  color: #fff !important;
  box-shadow: 0 8px 16px rgba(220, 53, 69, 0.22) !important;
}

.ticket-login-embedded-root :deep(.btn-danger:active),
.ticket-login-embedded-root :deep(.ticket-danger-btn:active),
.content-area :deep(.btn-danger:active),
.content-area :deep(.ticket-danger-btn:active) {
  background: #b21f2d !important;
  border-color: #a71d2a !important;
  color: #fff !important;
}

.ticket-login-embedded-root :deep(.btn-group > .btn),
.content-area :deep(.btn-group > .btn) {
  min-width: 0;
}

.ticket-section {
  padding: 18px !important;
}

.ticket-section-head {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px 16px;
  margin-bottom: 16px;
}

.ticket-section-head__text {
  flex: 1 1 240px;
  min-width: 0;
}

.ticket-section-title {
  margin: 0 0 8px !important;
  font-size: 1.1rem;
  line-height: 1.35;
  color: #1f2a44 !important;
}

.ticket-section-desc {
  margin: 0 !important;
  padding: 0;
  max-width: 720px;
  font-size: 13px;
  line-height: 1.65;
  color: #5c7098 !important;
}

.ticket-section-head__action {
  flex: 0 0 auto;
  margin-top: 2px;
}

.ticket-form-panel {
  padding: 16px;
  border: 1px solid #d7e6ff;
  border-radius: 12px;
  background: linear-gradient(180deg, #f7fbff 0%, #eef5ff 100%);
}

.ticket-field {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.ticket-field-label {
  display: block;
  margin: 0 !important;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  color: #4a628c !important;
}

.ticket-field-hint {
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  color: #7a8eaf;
}

.ticket-form-panel :deep(.form-control),
.ticket-form-panel :deep(.form-select) {
  min-height: 40px;
  padding: 0.5rem 0.75rem !important;
  border-color: #cfe0ff !important;
  background-color: #fff !important;
}

.ticket-form-panel :deep(textarea.form-control) {
  min-height: 96px;
  line-height: 1.5;
}

.ticket-form-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #d7e6ff;
}

.ticket-check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid #d7e6ff;
}

.ticket-check .form-check-input {
  margin-top: 0.2rem;
  flex: 0 0 auto;
}

.ticket-check .form-check-label {
  margin: 0;
  line-height: 1.5;
  color: #355b9f;
}

.ticket-cell-wrap {
  white-space: normal !important;
  word-break: break-word;
  min-width: 120px;
  max-width: 240px;
  line-height: 1.45;
}

.ticket-rule-sample {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 8px 12px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #cfe0ff;
  color: #2f5da5;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
}

.ticket-action-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.ticket-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.3;
  border: 1px solid transparent;
}

.ticket-status-badge.is-on {
  background: #e8f8ee;
  border-color: #b7e4c7;
  color: #1b7a3d;
}

.ticket-status-badge.is-off {
  background: #f1f4f8;
  border-color: #d5dde8;
  color: #5c6b82;
}

.city-paper-card {
  border-radius: 14px;
}

.city-paper-filter-row {
  padding: 14px 14px;
  margin-left: 0;
  margin-right: 0;
  row-gap: 12px !important;
  border: 1px solid #d7e6ff;
  border-radius: 12px;
  background: linear-gradient(180deg, #f7fbff 0%, #eef5ff 100%);
}

.city-paper-filter-row > [class*='col-'] {
  padding-left: 8px;
  padding-right: 8px;
}

.city-paper-result-shell {
  border-radius: 12px;
  border: 1px solid #dbe8ff;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  overflow: auto;
  padding: 4px;
}

.city-paper-card :deep(.form-control),
.city-paper-card :deep(.form-select) {
  border-color: #cfe0ff !important;
  background-color: #fff !important;
}

.city-paper-card :deep(.form-control:focus),
.city-paper-card :deep(.form-select:focus) {
  border-color: #79a7ff !important;
  box-shadow: 0 0 0 0.2rem rgba(82, 136, 240, 0.16) !important;
}

.city-paper-result-shell :deep(.modern-table thead th) {
  background: #edf4ff !important;
  color: #2a4f90 !important;
  padding: 12px 14px !important;
}

.city-paper-result-shell :deep(.modern-table tbody td) {
  padding: 12px 14px !important;
  vertical-align: middle !important;
}

.city-paper-result-shell :deep(.modern-table tbody tr:hover td) {
  background: #f3f8ff !important;
}

.ticket-bg-thumb-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ticket-bg-thumb {
  width: 64px;
  height: 40px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #cfe0ff;
  background: #edf4ff;
}

.ticket-bg-editor {
  padding: 12px 14px;
  border: 1px dashed #c6dbff;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.72);
}

.ticket-bg-preview-box {
  max-width: 360px;
}

.ticket-bg-preview-img {
  display: block;
  width: 100%;
  max-height: 160px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid #cfe0ff;
}

.ticket-bg-empty {
  font-size: 13px;
  color: #7a8eaf;
  line-height: 1.5;
}
</style>
